import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, Form, Message, Card } from 'semantic-ui-react';
import { DatetimeLocalInput } from '../../components/DatetimeLocalInput';
import { useNavigate, useParams } from 'react-router-dom';
import {
  API,
  copy,
  displayMemberGroup,
  modelDropdownOptions,
  lookupModelSummary,
  resolveTokenModel,
  showError,
  showSuccess,
  timestamp2string,
} from '../../helpers';
import { renderQuotaWithPrompt } from '../../helpers/render';
import { UserContext } from '../../context/User';

const EditToken = () => {
  const { t } = useTranslation();
  const [userState] = useContext(UserContext);
  const params = useParams();
  const tokenId = params.id;
  const isEdit = tokenId !== undefined;
  const [loading, setLoading] = useState(true);
  const [modelOptions, setModelOptions] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [legacyMultiWarning, setLegacyMultiWarning] = useState(false);
  const originInputs = {
    name: '',
    remain_quota: isEdit ? 0 : 500000,
    expired_time: -1,
    unlimited_quota: false,
    model: '',
    subnet: '',
  };
  const [inputs, setInputs] = useState(originInputs);
  const { name, remain_quota, expired_time, unlimited_quota, model } = inputs;
  const navigate = useNavigate();

  const userGroup = userState.user?.group || 'starter';
  const userGroupLabel = displayMemberGroup(userGroup);
  const selectedModelSummary = lookupModelSummary(availableModels, model);

  const handleInputChange = (e, { name, value }) => {
    if (name === 'expired_time') {
      const v =
        value === '' || value === null || value === undefined ? -1 : value;
      setInputs((inputs) => ({ ...inputs, expired_time: v }));
      return;
    }
    if (name === 'model') {
      setInputs((inputs) => ({ ...inputs, model: value || '' }));
      setLegacyMultiWarning(false);
      return;
    }
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const handleCancel = () => {
    navigate('/token');
  };

  const setExpiredTime = (month, day, hour, minute) => {
    let now = new Date();
    let timestamp = now.getTime() / 1000;
    let seconds = month * 30 * 24 * 60 * 60;
    seconds += day * 24 * 60 * 60;
    seconds += hour * 60 * 60;
    seconds += minute * 60;
    if (seconds !== 0) {
      timestamp += seconds;
      setInputs({ ...inputs, expired_time: timestamp2string(timestamp) });
    } else {
      setInputs({ ...inputs, expired_time: -1 });
    }
  };

  const setUnlimitedQuota = () => {
    setInputs({ ...inputs, unlimited_quota: !unlimited_quota });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const avRes = await API.get('/api/user/available_models');
        if (cancelled) return;
        const av = avRes.data;
        if (av.success && av.data) {
          setAvailableModels(av.data);
          setModelOptions(modelDropdownOptions(av.data));
        } else {
          showError(av.message || 'Failed to load models');
        }

        if (isEdit) {
          const tkRes = await API.get(`/api/token/${tokenId}`);
          const tk = tkRes.data;
          if (cancelled) return;
          if (tk.success && tk.data) {
            const data = { ...tk.data };
            if (data.expired_time !== -1) {
              data.expired_time = timestamp2string(data.expired_time);
            }
            const resolved = resolveTokenModel(data.models);
            if (resolved.multiLegacy) {
              setLegacyMultiWarning(true);
            }
            setInputs({
              name: data.name,
              remain_quota: data.remain_quota,
              expired_time: data.expired_time,
              unlimited_quota: data.unlimited_quota,
              model: resolved.model,
              subnet: data.subnet || '',
            });
          } else {
            showError(tk.message || 'Failed to load token');
          }
        }
      } catch (e) {
        showError(e.message || 'Network error');
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, tokenId]);

  const submit = async () => {
    if (!isEdit && inputs.name === '') return;
    const modelTrim = String(inputs.model || '').trim();
    if (!modelTrim) {
      showError(t('token.edit.messages.model_required'));
      return;
    }
    let localInputs = { ...inputs };
    localInputs.remain_quota = parseInt(localInputs.remain_quota, 10);
    if (localInputs.expired_time !== -1) {
      let time = Date.parse(localInputs.expired_time);
      if (isNaN(time)) {
        showError(t('token.edit.messages.expire_time_invalid'));
        return;
      }
      localInputs.expired_time = Math.ceil(time / 1000);
    }
    const payload = {
      name: localInputs.name,
      remain_quota: localInputs.remain_quota,
      expired_time: localInputs.expired_time,
      unlimited_quota: localInputs.unlimited_quota,
      models: modelTrim,
      subnet: localInputs.subnet,
    };
    let res;
    if (isEdit) {
      res = await API.put(`/api/token/`, {
        ...payload,
        id: parseInt(tokenId, 10),
      });
    } else {
      res = await API.post(`/api/token/`, payload);
    }
    const { success, message } = res.data;
    if (success) {
      if (isEdit) {
        showSuccess(t('token.edit.messages.update_success'));
      } else {
        showSuccess(t('token.edit.messages.create_success'));
        setInputs({
          name: '',
          remain_quota: 500000,
          expired_time: -1,
          unlimited_quota: false,
          model: '',
          subnet: '',
        });
        setLegacyMultiWarning(false);
      }
      navigate('/token');
    } else {
      showError(message);
    }
  };

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            {isEdit ? t('token.edit.title_edit') : t('token.edit.title_create')}
          </Card.Header>
          <Message info>
            {t('token.edit.scope_tier.hint_intro')}{' '}
            <Link to='/model-rates' style={{ color: '#00ff85' }}>
              {t('token.edit.scope_tier.link_rates')}
            </Link>
            {' · '}
            <Link to='/plans' style={{ color: '#00ff85' }}>
              {t('token.edit.scope_tier.link_plans')}
            </Link>
          </Message>
          <Message info style={{ marginTop: '0.75rem' }}>
            {t('token.edit.account_tier', { group: userGroupLabel })}
          </Message>
          <Message warning style={{ marginTop: '0.75rem' }}>
            {t('token.edit.api_http_hint')}{' '}
            <Link to='/docs' style={{ color: '#00ff85' }}>
              {t('token.edit.link_api_docs')}
            </Link>
          </Message>
          {legacyMultiWarning && (
            <Message warning>
              {t('token.edit.messages.multi_model_legacy')}
            </Message>
          )}
          <Form loading={loading} autoComplete='new-password'>
            <Form.Field>
              <Form.Input
                label={t('token.edit.name')}
                name='name'
                placeholder={t('token.edit.name_placeholder')}
                onChange={handleInputChange}
                value={name}
                autoComplete='new-password'
                required={!isEdit}
              />
            </Form.Field>

            <Form.Field>
              <Form.Dropdown
                label={t('token.edit.models')}
                placeholder={t('token.edit.models_placeholder')}
                name='model'
                fluid
                search
                selection
                clearable
                onChange={handleInputChange}
                value={model || ''}
                autoComplete='new-password'
                options={modelOptions}
              />
              {selectedModelSummary && (
                <Message info size='small' style={{ marginTop: '0.5rem' }}>
                  {selectedModelSummary}
                </Message>
              )}
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('token.edit.ip_limit')}
                name='subnet'
                placeholder={t('token.edit.ip_limit_placeholder')}
                onChange={handleInputChange}
                value={inputs.subnet}
                autoComplete='new-password'
              />
            </Form.Field>
            <Form.Field>
              <DatetimeLocalInput
                label={t('token.edit.expire_time')}
                name='expired_time'
                placeholder={t('token.edit.expire_time_placeholder')}
                onChange={handleInputChange}
                value={expired_time === -1 ? '' : expired_time}
                autoComplete='new-password'
              />
            </Form.Field>
            <div style={{ lineHeight: '40px' }}>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(0, 0, 0, 0);
                }}
              >
                {t('token.edit.buttons.never_expire')}
              </Button>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(1, 0, 0, 0);
                }}
              >
                {t('token.edit.buttons.expire_1_month')}
              </Button>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(0, 1, 0, 0);
                }}
              >
                {t('token.edit.buttons.expire_1_day')}
              </Button>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(0, 0, 1, 0);
                }}
              >
                {t('token.edit.buttons.expire_1_hour')}
              </Button>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(0, 0, 0, 1);
                }}
              >
                {t('token.edit.buttons.expire_1_minute')}
              </Button>
            </div>
            <Message>{t('token.edit.quota_notice')}</Message>
            <Form.Field>
              <Form.Input
                label={`${t('token.edit.quota')}${renderQuotaWithPrompt(
                  remain_quota,
                  t
                )}`}
                name='remain_quota'
                placeholder={t('token.edit.quota_placeholder')}
                onChange={handleInputChange}
                value={remain_quota}
                autoComplete='new-password'
                type='number'
                disabled={unlimited_quota}
              />
            </Form.Field>
            <Button
              type={'button'}
              onClick={() => {
                setUnlimitedQuota();
              }}
            >
              {unlimited_quota
                ? t('token.edit.buttons.cancel_unlimited')
                : t('token.edit.buttons.unlimited_quota')}
            </Button>
            <Button floated='right' positive onClick={submit}>
              {t('token.edit.buttons.submit')}
            </Button>
            <Button floated='right' onClick={handleCancel}>
              {t('token.edit.buttons.cancel')}
            </Button>
          </Form>
        </Card.Content>
      </Card>
    </div>
  );
};

export default EditToken;
